import {
  CreditCheckResultEnum,
  InstallationOptionsEnum,
} from '@updater/consumer-graph';
import { useGetEstimatedActivationDate } from 'flows/tvi/hooks/use-get-estimated-activation-date';
import { useReservation } from '@updater/ui-widgets';
import type { Address, CreditCard, PiiTokenizedData } from 'flows/tvi/types';
import { useEffect } from 'react';
import { useUserData } from 'flows/core/hooks/use-profile';
import { createFormStore } from '../common';
import { useXfinityCreditCheck } from 'flows/tvi/hooks/use-xfinity-credit-check';
import { SMS_OPT_IN_VALUE } from 'flows/tvi/components/review/utils/constants';

export type XFinityFormData = {
  flexDevices?: string;
  internetModemChoice?:
    | 'internetModemOwned'
    | 'xfinityGateway'
    | 'internetModemOwned';
  asyncCreditCheck: {
    creditCheckResult?: CreditCheckResultEnum | 'not_started';
    creditCheckMethod?: 'autopay' | 'deposit' | 'authorize-credit-check';
    dobPiiToken?: string;
    ssnPiiToken?: string;
  };
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  smsOptIn: 'sms'[];
  priorAddress?: Address;
  creditCard?: Partial<CreditCard>;
  installationType?: InstallationOptionsEnum;
  installationDate?: string;
  installationTimeWindow?:
    | '8am to 10am'
    | '10am to 12pm'
    | '1pm to 3pm'
    | '3pm to 5pm';
  // TODO: Remove after dob_ssn is productionized
  dob: string | PiiTokenizedData;
  // TODO: Remove after dob_ssn is productionized
  ssn?: PiiTokenizedData;
  serviceTransferDates?: {
    dateOrderIsValid?: boolean;
    reconnectDate?: string;
    disconnectDate?: string;
  };
  tvEquipmentChoice?: 'x1-tv-box' | 'customer-owned';
};

export enum XFinityPages {
  MODEMS = 'modems',
  PAYMENT = 'payment-method',
  OPTION = 'payment-option',
  INFO = 'payment-info',
  REVIEW = 'buy/review',
  DOB = 'dob',
  SUBMISSION = 'submission',
  ERROR = 'error',
  SCHEDULE_CONNECTION = 'schedule-connection',
}

export const omitKeysForLogging = [
  'dob',
  'ssn',
  'creditCard',
  'dobPiiToken',
  'ssnPiiToken',
];

export const useStore = createFormStore<XFinityFormData, XFinityPages>({
  provider: 'xfinity',
  initialPageRoute: XFinityPages.MODEMS,
  initialFormData: {},
});

export const getXFinitiyFormState = () => useStore.getState();

// TODO: add TV equipment & in transform props
export const useXFinityFormStore = (...args: Parameters<typeof useStore>) => {
  const status = useStore((state) => state.status);
  const initializeFormData = useStore((state) => state.initializeFormData);
  const { loading: userDataLoading, user } = useUserData();
  const reservationData = useReservation({
    moveId: user?.currentMove?.id ?? '',
  });

  const {
    creditCheckResult,
    ssnPiiToken,
    dobPiiToken,
    loading: xfinityCreditCheckLoading,
  } = useXfinityCreditCheck();

  const activationDefaults = useGetEstimatedActivationDate();

  const hasHydrated = useStore.persist.hasHydrated();

  useEffect(() => {
    if (
      !userDataLoading &&
      !xfinityCreditCheckLoading &&
      !reservationData?.loading &&
      hasHydrated &&
      status !== 'loaded'
    ) {
      const installationTypeFromReservation =
        reservationData?.offer?.options?.installationOptions?.type ??
        InstallationOptionsEnum.self;

      const isTransferReservation =
        reservationData?.reservationInfo?.preferences?.flow === 'TRANSFER';

      const defaultInstallationType =
        installationTypeFromReservation ?? InstallationOptionsEnum.self;

      const installationType = !isTransferReservation
        ? defaultInstallationType
        : undefined;

      const { firstName, lastName, email, phone, currentMove } = user || {};
      const { unit, state, street, city, postalCode } =
        currentMove?.fromAddress || {};

      const nonTransferDefaultCreditCheckMethod =
        creditCheckResult === CreditCheckResultEnum.passed
          ? 'authorize-credit-check'
          : 'autopay';

      // Only if TV bundle provide equipment value
      const defaultTvEquipmentChoice: XFinityFormData['tvEquipmentChoice'] =
        reservationData?.offer?.products?.tv ? 'customer-owned' : undefined;

      const initialData: Partial<XFinityFormData> = {
        internetModemChoice: !isTransferReservation
          ? 'xfinityGateway'
          : undefined,
        asyncCreditCheck: {
          creditCheckResult: creditCheckResult ?? 'not_started',
          creditCheckMethod: !isTransferReservation
            ? nonTransferDefaultCreditCheckMethod
            : undefined,
          ssnPiiToken,
          dobPiiToken,
        },
        installationType,
        installationDate: activationDefaults.estimatedDate ?? '',
        installationTimeWindow:
          installationType === InstallationOptionsEnum.self
            ? undefined
            : activationDefaults.defaultTimeWindow,
        tvEquipmentChoice: defaultTvEquipmentChoice,
        // Removing flexDevice for now until its pricing is implemented
        // Reference story https://updater.atlassian.net/browse/UPA-6
        // flexDevices: 'one',
        firstName: firstName ?? undefined,
        lastName: lastName ?? undefined,
        email: email ?? undefined,
        phone: phone ?? undefined,
        smsOptIn: [SMS_OPT_IN_VALUE],
        creditCard: !isTransferReservation
          ? {
              firstName: firstName ?? undefined,
              lastName: lastName ?? undefined,
              address: {
                street: street ?? '',
                unit: unit ?? '',
                city: city ?? '',
                state: state ?? '',
                postalCode: postalCode ?? '',
              },
            }
          : undefined,
        priorAddress: !isTransferReservation
          ? <Address>{
              street: user?.currentMove?.fromAddress.street ?? undefined,
              unit: user?.currentMove?.fromAddress.unit ?? '',
              city: user?.currentMove?.fromAddress.city ?? undefined,
              state: user?.currentMove?.fromAddress.state ?? undefined,
              postalCode:
                user?.currentMove?.fromAddress.postalCode ?? undefined,
            }
          : undefined,
      };

      initializeFormData({
        formData: initialData,
        offerName: reservationData?.offer?.name,
      });
    }
  }, [
    status,
    user,
    userDataLoading,
    hasHydrated,
    initializeFormData,
    reservationData?.offer?.products?.tv,
    reservationData?.loading,
    activationDefaults,
    xfinityCreditCheckLoading,
    creditCheckResult,
    ssnPiiToken,
    dobPiiToken,
  ]);

  return useStore(...args);
};
